import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { FormDrawerKey, FormDrawerParams } from '@/app/surfaces/form-drawer-registry.js';

export interface FormDrawerStackEntry {
  id: string;
  key: FormDrawerKey;
  params: FormDrawerParams;
}

export interface FormDrawerContextValue {
  stack: FormDrawerStackEntry[];
  openFormDrawer: (key: FormDrawerKey, params?: FormDrawerParams) => string;
  closeFormDrawer: (entryId?: string) => void;
  closeAllFormDrawers: () => void;
}

export const FormDrawerContext = createContext<FormDrawerContextValue | null>(null);

export function useFormDrawerContext(): FormDrawerContextValue {
  const value = useContext(FormDrawerContext);

  if (!value) {
    throw new Error('useFormDrawerContext must be used within FormDrawerProvider');
  }

  return value;
}

interface FormDrawerProviderProps {
  children: ReactNode;
}

function createDrawerId(): string {
  return crypto.randomUUID();
}

export function FormDrawerProvider({ children }: FormDrawerProviderProps) {
  const [stack, setStack] = useState<FormDrawerStackEntry[]>([]);

  const openFormDrawer = useCallback((key: FormDrawerKey, params: FormDrawerParams = {}) => {
    const id = createDrawerId();
    setStack((current) => [...current, { id, key, params }]);
    return id;
  }, []);

  const closeFormDrawer = useCallback((entryId?: string) => {
    setStack((current) => {
      if (!entryId) {
        return current.slice(0, -1);
      }

      return current.filter((entry) => entry.id !== entryId);
    });
  }, []);

  const closeAllFormDrawers = useCallback(() => {
    setStack([]);
  }, []);

  const value = useMemo<FormDrawerContextValue>(
    () => ({
      stack,
      openFormDrawer,
      closeFormDrawer,
      closeAllFormDrawers,
    }),
    [closeAllFormDrawers, closeFormDrawer, openFormDrawer, stack],
  );

  return <FormDrawerContext.Provider value={value}>{children}</FormDrawerContext.Provider>;
}
